import NProgress from 'nprogress';
import styled from '@emotion/styled';
import { Button } from 'react-bootstrap';
import React, { useContext, useEffect } from 'react';

import colours from '~/utils/colours';
import useReactRouter from 'use-react-router';
import { createRestApi } from '~/App/Api/rest';
import { alertAsync } from '~/App/Components/Modals/Alert';
import { confirmAsync } from '~/App/Components/Modals/Confirm';
import EditorContext, { EditorContextProps } from '../Editor/Context';

const ChangesContainer = styled.div`
  display: flex;
  align-items: center;
  color: ${colours.greyMediumDark};

  @media (max-width: 640px) {
    margin-top: 10px;
    flex-direction: row-reverse;
    justify-content: flex-end;
  }

  @media (max-width: 480px) {
    flex-wrap: wrap;
    flex-direction: initial;
    justify-content: center;

    > div {
      flex: 1 1 100%;
      text-align: center;
      margin-bottom: 10px;
    }

    button {
      margin: 0;

      &:first-of-type {
        margin-right: 10px;
      }
    }
  }
`;

const StyledButton = styled(Button)`
  margin-left: 15px;
  font-weight: bold !important;
  color: #42332a !important;
`;

export interface EditorButtonsProps {
  isSaving: boolean;
  onPrimaryButtonClick: (shouldSave?: boolean) => void;
}

export default function EditorButtons({
  isSaving,
  onPrimaryButtonClick,
}: EditorButtonsProps) {
  const context: EditorContextProps = useContext(EditorContext);
  const { hasChanges, hasUpdated } = context;
  const { match } = useReactRouter<{ id: string }>();

  async function handleDeleteClick() {
    if (
      await confirmAsync(
        'Are you sure you want to delete this message template?'
      )
    ) {
      const api = createRestApi('/api/v3/message-templates/');

      try {
        await api.delete(match.params.id);
      } catch (error) {
        if (error.response.status === 400) {
          await alertAsync(<span>{error.response.data.message}</span>);

          return;
        } else {
          throw error;
        }
      }

      window.location.replace('/message-templates');
    }
  }

  useEffect(() => {
    if (isSaving) {
      NProgress.start();
    } else {
      NProgress.done();
    }
  }, [isSaving]);

  function getChangesText() {
    if (hasChanges) {
      if (isSaving) {
        return 'Saving...';
      }

      return 'There are unsaved changes';
    }

    return hasUpdated && 'All changes saved';
  }

  return (
    <ChangesContainer>
      <div>{getChangesText()}</div>

      <StyledButton bsStyle='info' onClick={handleDeleteClick}>
        Delete
      </StyledButton>
      <StyledButton
        className={'btn-important'}
        onClick={() => onPrimaryButtonClick(hasChanges)}
      >
        {hasChanges ? 'Save & close' : 'Close'}
      </StyledButton>
    </ChangesContainer>
  );
}
